import { useQuery } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { queryKeys } from '../keys'
import { queryGet } from '../utils/request'

export interface AnalyticsQueryParams {
  date_start: string
  date_end: string
  environment?: 'production' | 'homolog'
  compare_previous?: boolean
  tenant_id?: number
}

export interface MetricValue {
  current: number
  previous: number | null
  change_percent: number | null
}

export interface RevenueSummaryResponse {
  total_revenue: MetricValue
  total_nfces: MetricValue
  avg_ticket: MetricValue
  total_products: MetricValue
  total_discounts: MetricValue
  total_other_expenses: MetricValue
}

export interface RevenueOverTimeEntry {
  period: string
  revenue: number
  nfces: number
}

export interface RevenueOverTimeResponse {
  granularity: string
  data: RevenueOverTimeEntry[]
}

export interface RevenueByCompanyEntry {
  company_id: number
  company_name: string
  revenue: number
  nfce_count: number
}

export interface RevenueByCompanyResponse {
  data: RevenueByCompanyEntry[]
}

export interface StatusDistributionEntry {
  status: string
  label: string
  count: number
  percentage: number
}

export interface OperationsStatusResponse {
  data: StatusDistributionEntry[]
}

export interface TopProductEntry {
  rank: number
  codigo: string
  ean: string
  descricao: string
  ncm: string
  cfop: string
  quantity_sold: number
  revenue: number
  average_price: number
}

export interface TopProductsResponse {
  order_by: string
  data: TopProductEntry[]
}

const CACHE_TIME = 5 * 60 * 1000

export const useRevenueSummaryQuery = (
  params: MaybeRefOrGetter<AnalyticsQueryParams | null>,
) => {
  return useQuery({
    queryKey: computed(() => queryKeys.analyticsRevenueSummary(toValue(params))),
    queryFn: async (): Promise<RevenueSummaryResponse> => {
      const p = toValue(params)
      return queryGet<RevenueSummaryResponse>('/v2/analytics/revenue/summary', { params: p })
    },
    enabled: computed(() => {
      const p = toValue(params)
      return !!(p?.date_start && p?.date_end)
    }),
    staleTime: CACHE_TIME,
  })
}

export const useRevenueOverTimeQuery = (
  params: MaybeRefOrGetter<AnalyticsQueryParams | null>,
) => {
  return useQuery({
    queryKey: computed(() => queryKeys.analyticsRevenueOverTime(toValue(params))),
    queryFn: async (): Promise<RevenueOverTimeResponse> => {
      const p = toValue(params)
      return queryGet<RevenueOverTimeResponse>('/v2/analytics/revenue/over-time', { params: p })
    },
    enabled: computed(() => {
      const p = toValue(params)
      return !!(p?.date_start && p?.date_end)
    }),
    staleTime: CACHE_TIME,
  })
}

export const useRevenueByCompanyQuery = (
  params: MaybeRefOrGetter<AnalyticsQueryParams | null>,
) => {
  return useQuery({
    queryKey: computed(() => queryKeys.analyticsRevenueByCompany(toValue(params))),
    queryFn: async (): Promise<RevenueByCompanyResponse> => {
      const p = toValue(params)
      return queryGet<RevenueByCompanyResponse>('/v2/analytics/revenue/by-company', { params: p })
    },
    enabled: computed(() => {
      const p = toValue(params)
      return !!(p?.date_start && p?.date_end)
    }),
    staleTime: CACHE_TIME,
  })
}

export const useOperationsStatusQuery = (
  params: MaybeRefOrGetter<AnalyticsQueryParams | null>,
) => {
  return useQuery({
    queryKey: computed(() => queryKeys.analyticsOperationsStatus(toValue(params))),
    queryFn: async (): Promise<OperationsStatusResponse> => {
      const p = toValue(params)
      return queryGet<OperationsStatusResponse>('/v2/analytics/operations/status-distribution', { params: p })
    },
    enabled: computed(() => {
      const p = toValue(params)
      return !!(p?.date_start && p?.date_end)
    }),
    staleTime: CACHE_TIME,
  })
}

export const useTopProductsQuery = (
  params: MaybeRefOrGetter<AnalyticsQueryParams | null>,
) => {
  return useQuery({
    queryKey: computed(() => queryKeys.analyticsTopProducts(toValue(params))),
    queryFn: async (): Promise<TopProductsResponse> => {
      const p = toValue(params)
      return queryGet<TopProductsResponse>('/v2/analytics/products/top', { params: p })
    },
    enabled: computed(() => {
      const p = toValue(params)
      return !!(p?.date_start && p?.date_end)
    }),
    staleTime: CACHE_TIME,
  })
}
